class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.closeTimeout = null;
    this.hoverOpenedAt = 0;
    this.hoverCloseDelay = 70;
    this.hoverClickGuardDelay = 500;
    this.pointerPosition = { x: null, y: null };
    this.documentPointerMoveHandler = this.onDocumentPointerMove.bind(this);
    this.documentScrollHandler = this.onDocumentScroll.bind(this);
    this.onHoverEnterHandler = this.onHoverEnter.bind(this);
    this.onHoverLeaveHandler = this.onHoverLeave.bind(this);
    this.onSummaryClickHandler = this.onSummaryClick.bind(this);

    this.summary.addEventListener('mouseenter', this.onHoverEnterHandler);
    this.summary.addEventListener('mouseleave', this.onHoverLeaveHandler);
    this.content.addEventListener('mouseenter', this.onHoverEnterHandler);
    this.content.addEventListener('mouseleave', this.onHoverLeaveHandler);
    this.summary.addEventListener('click', this.onSummaryClickHandler);
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  disconnectedCallback() {
    document.removeEventListener('pointermove', this.documentPointerMoveHandler);
    document.removeEventListener('scroll', this.documentScrollHandler, true);
    window.removeEventListener('wheel', this.documentScrollHandler);
    window.clearTimeout(this.closeTimeout);
    this.summary.removeEventListener('mouseenter', this.onHoverEnterHandler);
    this.summary.removeEventListener('mouseleave', this.onHoverLeaveHandler);
    this.content.removeEventListener('mouseenter', this.onHoverEnterHandler);
    this.content.removeEventListener('mouseleave', this.onHoverLeaveHandler);
    this.summary.removeEventListener('click', this.onSummaryClickHandler);
  }

  isDesktopViewport() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  openMenu(source = 'hover') {
    const wasOpen = this.mainDetailsToggle.hasAttribute('open');
    document.removeEventListener('pointermove', this.documentPointerMoveHandler);
    document.removeEventListener('scroll', this.documentScrollHandler, true);
    window.removeEventListener('wheel', this.documentScrollHandler);
    this.closeSiblingMenus();
    this.mainDetailsToggle.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', true);
    if (!wasOpen && source === 'hover') {
      this.hoverOpenedAt = window.performance.now();
    } else if (source !== 'hover') {
      this.hoverOpenedAt = 0;
    }
    document.addEventListener('pointermove', this.documentPointerMoveHandler);
    document.addEventListener('scroll', this.documentScrollHandler, true);
    window.addEventListener('wheel', this.documentScrollHandler, { passive: true });
  }

  close() {
    document.removeEventListener('pointermove', this.documentPointerMoveHandler);
    document.removeEventListener('scroll', this.documentScrollHandler, true);
    window.removeEventListener('wheel', this.documentScrollHandler);
    window.clearTimeout(this.closeTimeout);
    this.hoverOpenedAt = 0;
    super.close();
  }

  closeSiblingMenus() {
    const headerMenu = this.closest('.header__inline-menu');
    if (!headerMenu) return;

    headerMenu.querySelectorAll('header-menu details[open]').forEach((openDetails) => {
      if (openDetails === this.mainDetailsToggle) return;
      openDetails.closest('header-menu')?.close();
    });
  }

  onHoverEnter() {
    if (!this.isDesktopViewport()) return;
    window.clearTimeout(this.closeTimeout);
    this.openMenu('hover');
  }

  onHoverLeave() {
    if (!this.isDesktopViewport()) return;
    window.clearTimeout(this.closeTimeout);
    this.closeTimeout = window.setTimeout(() => {
      this.close();
    }, this.hoverCloseDelay);
  }

  onSummaryClick(event) {
    if (!this.isDesktopViewport()) return;

    event.preventDefault();
    window.clearTimeout(this.closeTimeout);

    if (this.mainDetailsToggle.hasAttribute('open')) {
      if (window.performance.now() - this.hoverOpenedAt < this.hoverClickGuardDelay) {
        return;
      }
      this.close();
      return;
    }

    this.openMenu('click');
  }

  isPointWithinRect(rect, x, y, padding = 0) {
    return (
      x >= rect.left - padding &&
      x <= rect.right + padding &&
      y >= rect.top - padding &&
      y <= rect.bottom + padding
    );
  }

  isPointWithinHoverSafeZone(x, y) {
    const summaryRect = this.summary.getBoundingClientRect();
    const contentRect = this.content.getBoundingClientRect();
    const bridgeTop = summaryRect.bottom - 4;
    const bridgeBottom = contentRect.top + 6;
    const bridgeLeft = Math.min(summaryRect.left, contentRect.left) - 12;
    const bridgeRight = Math.max(summaryRect.right, contentRect.right) + 12;

    const insideSummary = this.isPointWithinRect(summaryRect, x, y, 10);
    const insideContent = this.isPointWithinRect(contentRect, x, y, 10);
    const insideBridge =
      x >= bridgeLeft &&
      x <= bridgeRight &&
      y >= bridgeTop &&
      y <= bridgeBottom;

    return insideSummary || insideContent || insideBridge;
  }

  onDocumentPointerMove(event) {
    if (!this.isDesktopViewport() || !this.mainDetailsToggle.hasAttribute('open')) return;

    this.pointerPosition = {
      x: event.clientX,
      y: event.clientY,
    };

    if (this.isPointWithinHoverSafeZone(this.pointerPosition.x, this.pointerPosition.y)) {
      window.clearTimeout(this.closeTimeout);
      return;
    }

    this.onHoverLeave();
  }

  onDocumentScroll() {
    if (!this.isDesktopViewport() || !this.mainDetailsToggle.hasAttribute('open')) return;

    const { x, y } = this.pointerPosition;

    if (x === null || y === null || !this.isPointWithinHoverSafeZone(x, y)) {
      this.close();
    }
  }
}

customElements.define('header-menu', HeaderMenu);
